/**
 * The command palette (Ctrl+K or ⌘K, or /): jump to any page, lead, audit or partner by typing a
 * few letters, and run common commands. Built on the ARIA combobox pattern: the search box owns a
 * listbox, the arrow keys move the active option, Enter opens it and Escape closes the palette.
 */
import type { Db } from "../data/db.ts";
import { h } from "./dom.ts";
import { icon, type IconName } from "./icons.ts";
import { PAGES } from "./nav.ts";
import { prefs, recent, setPrefs } from "./prefs.ts";
import { celebrate } from "./katana.ts";
import { showShortcuts } from "./shortcuts.ts";
import { toast } from "./toast.ts";
import { langAttrs, t } from "./i18n.ts";

export interface Command {
  id: string;
  group: string;
  label: string;
  /** The English label, so English words always find a command */
  en?: string;
  sub?: string;
  icon: IconName;
  keys?: string[];
  words?: string;
  run: () => void;
}

/** Opens a collapsed panel on the current page by its summary text, and puts the cursor in it. */
export function openPanel(summary: string): void {
  const d = [...document.querySelectorAll<HTMLDetailsElement>("main details")].find((x) => x.querySelector("summary")?.textContent?.trim() === summary);
  if (!d) return;
  d.open = true;
  d.scrollIntoView({ block: "start", behavior: "smooth" });
  d.querySelector<HTMLElement>("input:not([type=hidden]), select, textarea")?.focus({ preventScroll: true });
}

const go = (href: string, then?: () => void) => {
  if (location.hash === href) then?.();
  else {
    if (then) window.addEventListener("ryoma:routed", () => setTimeout(then, 30), { once: true });
    location.hash = href;
  }
};

export function baseCommands(db: Db): Command[] {
  const pages: Command[] = PAGES.map((p) => ({ id: `page-${p.key}`, group: "Pages", label: t(p.label), en: p.label, icon: p.icon, keys: ["G", p.go.toUpperCase()], words: `${p.label} ${p.also ?? ""}`, run: () => go(p.href) }));
  const actions: Command[] = [
    { id: "new-lead", group: "Actions", label: t("Add a lead or prospect"), en: "Add a lead or prospect", icon: "plus", keys: ["N"], words: "new create enquiry", run: () => go("#/leads", () => openPanel("Add a prospect or lead")) },
    { id: "import", group: "Actions", label: t("Import a target list (CSV)"), en: "Import a target list (CSV)", icon: "download", words: "upload csv prospects", run: () => go("#/leads", () => openPanel("Import a target list (CSV)")) },
    { id: "new-audit", group: "Actions", label: t("Start a new audit"), en: "Start a new audit", icon: "audits", words: "create client", run: () => go("#/audits", () => openPanel("New audit")) },
    { id: "new-partner", group: "Actions", label: t("Add a partner"), en: "Add a partner", icon: "partners", words: "referral", run: () => go("#/partners", () => openPanel("Add a partner")) },
    { id: "theme-light", group: "Preferences", label: t("Use the light theme"), en: "Use the light theme", icon: "sun", words: "appearance colour", run: () => { setPrefs({ theme: "light" }); toast("Light theme"); } },
    { id: "theme-dark", group: "Preferences", label: t("Use the dark theme"), en: "Use the dark theme", icon: "moon", words: "appearance colour night", run: () => { setPrefs({ theme: "dark" }); toast("Dark theme"); } },
    { id: "theme-system", group: "Preferences", label: t("Match the computer's theme"), en: "Match the computer's theme", icon: "monitor", words: "appearance system auto", run: () => { setPrefs({ theme: "system" }); toast("Theme matches the computer"); } },
    { id: "density", group: "Preferences", label: t(prefs().density === "compact" ? "Comfortable spacing" : "Compact spacing"), en: prefs().density === "compact" ? "Comfortable spacing" : "Compact spacing", icon: "palette", words: "density dense tables", run: () => { setPrefs({ density: prefs().density === "compact" ? "comfortable" : "compact" }); toast(prefs().density === "compact" ? "Compact spacing" : "Comfortable spacing"); } },
    { id: "sidebar", group: "Preferences", label: t(prefs().sidebarCollapsed ? "Expand the sidebar" : "Collapse the sidebar"), en: prefs().sidebarCollapsed ? "Expand the sidebar" : "Collapse the sidebar", icon: prefs().sidebarCollapsed ? "expand" : "collapse", keys: ["["], words: "menu narrow", run: () => setPrefs({ sidebarCollapsed: !prefs().sidebarCollapsed }) },
    { id: "katana", group: "Preferences", label: t("Preview the katana cut"), en: "Preview the katana cut", icon: "katana", words: "celebrate animation effect", run: () => celebrate("Client won") },
    { id: "shortcuts", group: "Help", label: t("Keyboard shortcuts"), en: "Keyboard shortcuts", icon: "keyboard", keys: ["?"], words: "keys help", run: () => showShortcuts() },
    { id: "signout", group: "Help", label: t("Sign out"), en: "Sign out", icon: "logout", words: "sign out log out leave", run: () => void db.signOut() },
  ];
  const recents: Command[] = recent().map((r) => ({ id: `recent-${r.href}`, group: "Recently viewed", label: r.title, sub: r.kind === "lead" ? t("Leads") : t("Audits"), icon: r.kind === "lead" ? "leads" : "audits", run: () => go(r.href) }));
  return [...recents, ...pages, ...actions];
}

/** Leads, audits and partners, loaded when the palette opens. */
async function recordCommands(db: Db): Promise<Command[]> {
  const { leadTitle } = await import("./leads.ts");
  const [leads, audits, partners] = await Promise.all([db.listLeads().catch(() => []), db.listAudits().catch(() => []), db.listPartners().catch(() => [])]);
  return [
    ...leads.map((l) => ({ id: `lead-${l.id}`, group: "Leads", label: leadTitle(l), sub: l.status.replace(/_/g, " "), icon: "leads" as const, words: [l.email, l.company, l.website, l.sector].filter(Boolean).join(" "), run: () => go(`#/lead/${l.id}`) })),
    ...audits.map((a) => ({ id: `audit-${a.id}`, group: "Audits", label: `${a.client_name}: ${a.title}`, sub: a.status.replace(/_/g, " "), icon: "audits" as const, run: () => go(`#/audit/${a.id}`) })),
    ...partners.map((p) => ({ id: `partner-${p.id}`, group: "Partners", label: p.name, sub: p.kind.replace(/_/g, " "), icon: "partners" as const, words: [p.contact_name, p.email].filter(Boolean).join(" "), run: () => go("#/partners", () => openPanel(p.name)) })),
  ];
}

/** Scores a command against the typed words: every word must match somewhere; label matches rank first. */
export function score(c: Pick<Command, "label" | "words" | "sub" | "group" | "en">, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 1;
  const label = c.label.toLowerCase();
  const hay = `${label} ${c.en ?? ""} ${c.sub ?? ""} ${c.words ?? ""} ${c.group}`.toLowerCase();
  let s = 0;
  for (const w of q.split(/\s+/)) {
    if (!hay.includes(w)) return 0;
    s += label.startsWith(w) ? 6 : new RegExp(`\\b${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(label) ? 4 : label.includes(w) ? 2 : 1;
  }
  return s;
}

let dialog: HTMLDialogElement | null = null;

export function openPalette(db: Db): void {
  if (dialog?.open) return;
  dialog?.remove();
  let commands = baseCommands(db);
  let shown: Command[] = [];
  let active = 0;
  const input = h("input", {
    type: "text", role: "combobox", "aria-expanded": "true", "aria-controls": "palette-list", "aria-autocomplete": "list",
    "aria-label": t("Search or type a command…"), placeholder: t("Search or type a command…"), autocomplete: "off", spellcheck: "false",
  });
  const list = h("ul", { class: "palette-list", id: "palette-list", role: "listbox", "aria-label": "Results" });
  const live = h("div", { class: "sr-only", "aria-live": "polite" });
  dialog = h("dialog", { class: "palette", "aria-label": "Command palette", ...langAttrs() },
    h("div", { class: "palette-search" }, icon("search"), input, h("kbd", null, "Esc")),
    list,
    h("div", { class: "palette-foot", "aria-hidden": "true" },
      h("span", null, h("kbd", null, "↑"), " ", h("kbd", null, "↓"), " ", t("move")), h("span", null, h("kbd", null, "Enter"), " ", t("open")), h("span", null, h("kbd", null, "Esc"), " ", t("close"))),
    live,
  );

  const paint = () => {
    const q = input.value;
    const ranked = commands.map((c) => ({ c, s: score(c, q) })).filter((x) => x.s > 0);
    // Without a query: recent, pages and actions only (records appear once you type).
    const pool = q.trim() ? ranked.sort((a, b) => b.s - a.s) : ranked.filter((x) => !["Leads", "Audits", "Partners"].includes(x.c.group));
    shown = pool.slice(0, 40).map((x) => x.c);
    if (q.trim()) {
      // Keep the best match on top, then group the rest.
      const order = [...new Set(shown.map((c) => c.group))];
      shown.sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group));
    }
    active = Math.min(active, Math.max(0, shown.length - 1));
    list.replaceChildren();
    if (!shown.length) {
      list.append(h("li", { class: "palette-empty", role: "presentation" }, `${t("Nothing matches")}: “${q}”`));
      input.removeAttribute("aria-activedescendant");
      live.textContent = "No results";
      return;
    }
    let group = "";
    shown.forEach((c, i) => {
      if (c.group !== group) {
        group = c.group;
        list.append(h("li", { class: "palette-group", role: "presentation" }, t(group)));
      }
      list.append(h("li", { class: "palette-item", role: "option", id: `pal-${i}`, "aria-selected": String(i === active), onClick: () => choose(i), onMousemove: () => { if (active !== i) { active = i; mark(); } } },
        icon(c.icon), h("span", null, c.label, c.sub ? h("span", { class: "sub" }, c.sub) : null),
        c.keys ? h("span", { class: "kbd", "aria-hidden": "true" }, c.keys.map((k) => h("kbd", null, k))) : null,
      ));
    });
    mark();
    live.textContent = `${shown.length} result${shown.length === 1 ? "" : "s"}`;
  };
  const mark = () => {
    list.querySelectorAll<HTMLElement>(".palette-item").forEach((el) => el.setAttribute("aria-selected", String(el.id === `pal-${active}`)));
    input.setAttribute("aria-activedescendant", `pal-${active}`);
    list.querySelector(`#pal-${active}`)?.scrollIntoView({ block: "nearest" });
  };
  const close = () => dialog?.close();
  const choose = (i: number) => {
    const c = shown[i];
    if (!c) return;
    close();
    c.run();
  };
  input.addEventListener("input", () => { active = 0; paint(); });
  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); active = (active + 1) % Math.max(1, shown.length); mark(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); active = (active - 1 + shown.length) % Math.max(1, shown.length); mark(); }
    else if (e.key === "Home" && e.ctrlKey) { e.preventDefault(); active = 0; mark(); }
    else if (e.key === "Enter") { e.preventDefault(); choose(active); }
  });
  dialog.addEventListener("click", (e) => { if (e.target === dialog) close(); });
  dialog.addEventListener("close", () => { const d = dialog; setTimeout(() => d?.remove(), 0); });

  document.body.append(dialog);
  dialog.showModal();
  input.focus();
  paint();
  void recordCommands(db).then((recs) => { commands = [...commands, ...recs]; if (dialog?.open) paint(); });
}
